/* Plans — the only source for prices, quotas and gates.
 *
 * Standing ruling (a): no client fallbacks. Nothing here supplies a default
 * price, quota or gate; a missing field is reported, never invented.
 * Standing ruling (f): the contact gate is `contact_enabled` on the plan.
 */

import { apiGet, authPost } from "./client";

export type PlanRow = {
  id: number;
  name: string;
  price: number | null;
  max_properties: number | null;
  enabled: boolean;
  ai_monthly_limit: number | null;
  contact_enabled?: boolean;
  effective_price?: number | null;
  promo?: unknown;
};

export type Promo = {
  name?: string; discount_type?: string; discount_value?: number;
  message?: string; ends_at?: string;
};

export type Plan = {
  id: number;
  name: string;
  enabled: boolean;
  price: number | null;
  /** The server's computed price after any active promo. */
  effectivePrice: number | null;
  maxProperties: number | null;
  aiMonthlyLimit: number | null;
  /** Server-supplied promo, or null. Never synthesised -- honest signals. */
  promo: Promo | null;
  /** Undefined means the server did not send the field -- a config gap. */
  contactEnabled: boolean | undefined;
};

let reportedMissingGate = false;

/**
 * Whether the contact affordance may render, for a member on `planName`.
 * A guest has no plan, so `planName` is null and the answer comes from
 * whether the platform has the gate open at all.
 *
 * Returns undefined when the server did not report the field. Callers must
 * treat undefined as "unknown", never as false or true.
 */
export function contactEnabledFor(plans: Plan[], planName: string | null): boolean | undefined {
  if (plans.length === 0) return undefined;

  const missing = plans.filter((p) => p.contactEnabled === undefined);
  if (missing.length > 0) {
    if (!reportedMissingGate) {
      reportedMissingGate = true;
      console.warn(
        `[SouqAkar] /plans did not return contact_enabled for: ` +
        `${missing.map((p) => p.name).join(", ")}. The contact gate cannot be ` +
        `resolved and no default is assumed -- report to Mark. ` +
        `(standing rulings a and f)`,
      );
    }
    return undefined;
  }

  if (planName) {
    const mine = plans.find((p) => p.name.toLowerCase() === planName.toLowerCase());
    return mine?.contactEnabled;
  }
  // Guest: the affordance shows if any enabled plan opens contact, because
  // signing in is what turns it into a real action.
  return plans.some((p) => p.enabled && p.contactEnabled === true);
}

export async function fetchPlans(signal?: AbortSignal): Promise<Plan[]> {
  const rows = await apiGet<PlanRow>("plans", signal);
  return rows.map((r) => ({
    id: r.id,
    name: r.name,
    enabled: !!r.enabled,
    price: typeof r.price === "number" ? r.price : null,
    effectivePrice: typeof r.effective_price === "number" ? r.effective_price : null,
    maxProperties: r.max_properties ?? null,
    aiMonthlyLimit: r.ai_monthly_limit ?? null,
    promo: (r.promo as Promo | null) ?? null,
    contactEnabled: typeof r.contact_enabled === "boolean" ? r.contact_enabled : undefined,
  }));
}

/* ── Checkout ─────────────────────────────────────────────────────────────
 * POST /payments/checkout { plan, code? }. Card data never reaches this call
 * -- the contract is explicit, and there is no payment integration behind it
 * (gotchas 1). The worker answers with mock:true where nothing was charged,
 * and the confirmation says exactly that rather than printing an "amount
 * charged" for a payment that did not happen.
 */

export type CheckoutResponse = {
  ok?: boolean;
  plan?: string;
  price?: number | null;
  renews_at?: string | null;
  reference?: string | null;
  /** The worker's own words for what happened. */
  mock?: boolean;
  free?: boolean;
  effective_price?: number | null;
  error?: string;
  [k: string]: unknown;
};

export type CheckoutResult =
  | { ok: true; data: CheckoutResponse }
  | { ok: false; reason: "auth" | "error"; detail?: string };

/* `acknowledgeDowngrade` must reflect a real confirmation, not be hardcoded
 * true: v3.9.0 §3 answers 409 DOWNGRADE_REQUIRES_ACK precisely to catch a
 * client that did not ask. A client that always claims consent would defeat
 * the guard it is meant to cooperate with. Today's worker ignores the field;
 * tomorrow's requires it, and only the confirmed path sends it. */
export async function checkout(
  plan: string, code?: string, acknowledgeDowngrade = false,
): Promise<CheckoutResult> {
  const body: Record<string, unknown> = { plan };
  if (code && code.trim()) body.code = code.trim();
  if (acknowledgeDowngrade) body.acknowledge_downgrade = true;
  try {
    const rows = await authPost<CheckoutResponse>("payments/checkout", body, false);
    const data = (rows[0] ?? {}) as CheckoutResponse;
    if (data.ok === false) return { ok: false, reason: "error", detail: String(data.error ?? "") };
    return { ok: true, data };
  } catch (e) {
    const name = (e as Error)?.name;
    if (name === "UnauthorizedError") return { ok: false, reason: "auth" };
    return { ok: false, reason: "error", detail: String((e as Error)?.message ?? "").slice(0, 200) };
  }
}
